import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEO_TABS = [
  { id: "overview", label: "Vue d'ensemble" },
  { id: "search-console", label: "Search Console" },
  { id: "opportunities", label: "Opportunités" },
  { id: "issues", label: "Problèmes" },
  { id: "content", label: "Contenu" },
  { id: "keywords", label: "Mots-clés" },
  { id: "generate", label: "Générer page" },
  { id: "competitors", label: "Concurrents" },
  { id: "indexation", label: "Indexation" },
  { id: "technical", label: "Technique" },
];

const PERIODS = [7, 14, 28, 90];

const SERVICE_OPTIONS = [
  "Réparation de pavé uni",
  "Installation de pavé uni",
  "Nettoyage de pavé uni",
  "Scellant protecteur",
  "Réparation de muret et bordure",
  "Nivelage / remise à niveau",
  "Remplacement de joint sable",
  "Réparation trottoir",
  "Drainage / correction d'écoulement",
  "Aménagement paysager",
];

const INDEXATION_URLS = [
  { url: "https://reparation-pave-uni-rive-sud.com/", status: "Indexé" },
  { url: "https://www.reparation-pave-uni-rive-sud.com/", status: "Indexé" },
  {
    url: "https://reparation-pave-uni-rive-sud.com/reparation-de-pave-uni",
    status: "Indexé",
  },
  { url: "https://reparation-pave-uni-rive-sud.com/pave-uni-longueuil", status: "À indexer" },
  {
    url: "https://reparation-pave-uni-rive-sud.com/saint-jean-sur-richelieu",
    status: "À indexer",
  },
  { url: "https://reparation-pave-uni-rive-sud.com/chambly", status: "À indexer" },
  {
    url: "https://reparation-pave-uni-rive-sud.com/fissure-de-fondation",
    status: "À indexer",
  },
  { url: "https://reparation-pave-uni-rive-sud.com/longueuil", status: "À indexer" },
  { url: "https://reparation-pave-uni-rive-sud.com/pave-uni-boucherville", status: "À indexer" },
  { url: "https://reparation-pave-uni-rive-sud.com/pave-uni-rive-sud-1", status: "À indexer" },
];

const AdminSeoPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [period, setPeriod] = useState(28);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState({ connected: false, property: "" });
  const [data, setData] = useState({
    summary: { clicks: 0, impressions: 0, ctr: 0, position: 0 },
    daily: [],
    queries: [],
    pages: [],
    opportunities: [],
    issues: [],
    generated: [],
    sitemaps: [],
    competitors: [],
  });
  const [competitorUrl, setCompetitorUrl] = useState("");
  const [generateForm, setGenerateForm] = useState({
    language: "fr",
    city: "",
    service: "",
    primary_keyword: "",
    secondary_keywords: "",
    notes: "",
  });
  const [generatedHtml, setGeneratedHtml] = useState("");
  const [generating, setGenerating] = useState(false);

  const token = localStorage.getItem("admin_token");

  const headers = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const loadStatus = async () => {
    try {
      const response = await axios.get(`${API}/seo/status`, headers);
      setStatus(response.data);
    } catch (err) {
      setStatus({ connected: false, property: "" });
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/seo/overview?days=${period}`, headers);
      setData(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || "Impossible de charger les données SEO.");
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setError("");
    try {
      const response = await axios.get(`${API}/seo/auth/url`, headers);
      window.location.href = response.data.auth_url;
    } catch (err) {
      setError("Connexion Google impossible.");
    }
  };

  const addCompetitor = async () => {
    if (!competitorUrl.trim()) return;
    try {
      const response = await axios.post(
        `${API}/seo/competitors`,
        { url: competitorUrl.trim() },
        headers,
      );
      setData((prev) => ({
        ...prev,
        competitors: [...prev.competitors, response.data],
      }));
      setCompetitorUrl("");
    } catch (err) {
      setError("Impossible d'ajouter le concurrent.");
    }
  };

  const handleGenerateChange = (field, value) => {
    setGenerateForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    if (!token) return;
    setGenerating(true);
    setError("");
    try {
      const response = await axios.post(`${API}/seo/generate`, generateForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGeneratedHtml(response.data.html || "");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || "Impossible de générer la page.");
    } finally {
      setGenerating(false);
    }
  };

  const downloadGeneratedHtml = () => {
    if (!generatedHtml) return;
    const blob = new Blob([generatedHtml], { type: "text/html" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `page-${generateForm.city || "rive-sud"}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const removeCompetitor = async (id) => {
    try {
      await axios.delete(`${API}/seo/competitors/${id}`, headers);
      setData((prev) => ({
        ...prev,
        competitors: prev.competitors.filter((item) => item.id !== id),
      }));
    } catch (err) {
      setError("Impossible de supprimer le concurrent.");
    }
  };

  const exportCsv = () => {
    const headersRow = ["Type", "Label", "Clicks", "Impressions", "CTR", "Position"];
    const rows = [
      ...data.queries.map((row) => [
        "Mot-clé",
        row.query,
        row.clicks,
        row.impressions,
        row.ctr,
        row.position,
      ]),
      ...data.pages.map((row) => [
        "Page",
        row.page,
        row.clicks,
        row.impressions,
        row.ctr,
        row.position,
      ]),
    ];
    const csv = [headersRow, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `seo-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    loadStatus();
  }, [token]);

  useEffect(() => {
    if (status.connected) {
      loadData();
    }
  }, [status.connected, period]);

  const metricCards = [
    { label: "Clics", value: data.summary.clicks },
    { label: "Impressions", value: data.summary.impressions },
    { label: "CTR moyen", value: `${Math.round(data.summary.ctr * 100)}%` },
    { label: "Position moyenne", value: Number(data.summary.position || 0).toFixed(1) },
  ];

  const dailySeries = useMemo(() => {
    if (!data.daily || data.daily.length === 0) return [];
    return [...data.daily].sort((a, b) => (a.date > b.date ? 1 : -1));
  }, [data.daily]);

  const buildSparkline = (points) => {
    if (!points || points.length === 0) return "";
    const width = 220;
    const height = 60;
    const values = points.map((point) => point.value);
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    return values
      .map((value, index) => {
        const x = (index / Math.max(values.length - 1, 1)) * width;
        const y = height - ((value - min) / Math.max(max - min, 1)) * height;
        return `${x},${y}`;
      })
      .join(" ");
  };

  const sparklineData = useMemo(() => {
    return {
      clicks: dailySeries.map((row) => ({ date: row.date, value: row.clicks })),
      impressions: dailySeries.map((row) => ({ date: row.date, value: row.impressions })),
    };
  }, [dailySeries]);

  const alerts = useMemo(() => {
    const items = [];
    if (data.summary.impressions > 100 && data.summary.ctr < 0.02) {
      items.push({
        type: "warning",
        label: "CTR faible",
        detail: "Le CTR moyen est inférieur à 2% alors que les impressions sont élevées.",
      });
    }
    if (data.summary.position > 20) {
      items.push({
        type: "warning",
        label: "Position moyenne faible",
        detail: "La position moyenne dépasse 20. Optimiser les pages prioritaires.",
      });
    }
    if (data.opportunities.length > 0) {
      items.push({
        type: "info",
        label: "Opportunités détectées",
        detail: `${data.opportunities.length} mots-clés ont beaucoup d'impressions mais un CTR faible.`,
      });
    }
    if (data.issues.length > 0) {
      items.push({
        type: "info",
        label: "Pages à améliorer",
        detail: `${data.issues.length} pages ont une position moyenne > 20.`,
      });
    }
    if (dailySeries.length >= 14) {
      const last7 = dailySeries.slice(-7).reduce((sum, row) => sum + row.clicks, 0);
      const prev7 = dailySeries.slice(-14, -7).reduce((sum, row) => sum + row.clicks, 0);
      if (prev7 > 0 && last7 < prev7 * 0.7) {
        items.push({
          type: "warning",
          label: "Baisse de trafic",
          detail: "Les clics des 7 derniers jours ont baissé de plus de 30%.",
        });
      }
    }
    return items;
  }, [data.summary, data.opportunities, data.issues, dailySeries]);

  const renderTable = (rows, columns) => (
    <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-white/10">
            {columns.map((col) => (
              <TableHead key={col.key} className="text-slate-300">
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.id || row.query || row.page || index} className="border-white/10">
              {columns.map((col) => (
                <TableCell key={col.key} data-testid={`seo-cell-${col.key}-${index}`}>
                  {col.format ? col.format(row[col.key], row) : row[col.key] || "-"}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0b1020] text-slate-100" data-testid="admin-seo-dashboard">
      <header className="border-b border-white/10 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-secondary" data-testid="seo-dashboard-eyebrow">
              Tableau de bord SEO IA
            </p>
            <h1 className="text-2xl font-bold text-white" data-testid="seo-dashboard-title">
              Analyse SEO — Pavé uni
            </h1>
            <p className="text-sm text-slate-300" data-testid="seo-dashboard-subtitle">
              Données Google Search Console pour améliorer la visibilité locale.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              className="rounded-none uppercase tracking-wider font-bold border-white/30 text-white hover:bg-white/10"
              onClick={() => navigate("/admin")}
              data-testid="seo-back-leads"
            >
              Retour leads
            </Button>
            <Button
              variant="outline"
              className="rounded-none uppercase tracking-wider font-bold border-white/30 text-white hover:bg-white/10"
              onClick={exportCsv}
              data-testid="seo-export-button"
            >
              Export CSV
            </Button>
            <Button
              className="rounded-none uppercase tracking-wider font-bold bg-white text-slate-900 hover:bg-slate-200"
              onClick={loadData}
              data-testid="seo-refresh-button"
            >
              Lancer l'analyse
            </Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pb-6">
          <div className="flex flex-wrap gap-2" data-testid="seo-tabs">
            {SEO_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.3em] rounded-full border transition ${
                  activeTab === tab.id
                    ? "bg-secondary text-slate-950 border-secondary"
                    : "border-white/20 text-slate-300 hover:text-white"
                }`}
                data-testid={`seo-tab-${tab.id}`}
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-10">
        {activeTab === "overview" && (
          <div
            className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center flex flex-col items-center gap-4"
            data-testid="seo-overview-cta"
          >
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
              <span className="text-secondary text-xl">🔍</span>
            </div>
            <div>
              <p className="text-lg font-semibold text-white" data-testid="seo-overview-title">
                Prêt à analyser votre site
              </p>
              <p className="text-sm text-slate-300 mt-2" data-testid="seo-overview-subtitle">
                Lancez l'analyse pour obtenir un rapport SEO complet et des actions prioritaires.
              </p>
            </div>
            <Button
              className="rounded-none uppercase tracking-wider font-bold bg-white text-slate-900"
              onClick={status.connected ? loadData : handleConnect}
              data-testid="seo-overview-action"
            >
              {status.connected ? "Lancer l'analyse SEO" : "Connecter Google"}
            </Button>
          </div>
        )}

        {!status.connected && activeTab !== "overview" && (
          <div
            className="rounded-xl border border-white/10 bg-white/5 p-6"
            data-testid="seo-connect-card"
          >
            <p className="text-sm text-slate-300 mb-4">
              Connecte Google Search Console pour voir les données réelles.
            </p>
            <Button
              className="rounded-none uppercase tracking-wider font-bold bg-white text-slate-900"
              onClick={handleConnect}
              data-testid="seo-connect-button"
            >
              Connecter Google
            </Button>
          </div>
        )}

        {status.connected && (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="seo-periods">
              {PERIODS.map((value) => (
                <button
                  key={value}
                  onClick={() => setPeriod(value)}
                  className={`px-3 py-1 text-xs rounded-full border transition ${
                    period === value
                      ? "bg-white text-slate-900 border-white"
                      : "border-white/20 text-slate-300 hover:text-white"
                  }`}
                  data-testid={`seo-period-${value}`}
                  type="button"
                >
                  {value}j
                </button>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8" data-testid="seo-metrics">
              {metricCards.map((metric) => (
                <div
                  key={metric.label}
                  className="rounded-xl border border-white/10 bg-white/5 px-5 py-4"
                  data-testid={`seo-metric-${metric.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{metric.label}</p>
                  <p className="text-2xl font-semibold text-white mt-3">{metric.value}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {error && (
          <p className="text-sm text-red-400 mb-4" data-testid="seo-error">
            {error}
          </p>
        )}

        {loading && status.connected ? (
          <p data-testid="seo-loading">Chargement...</p>
        ) : (
          <>
            {activeTab === "search-console" && (
              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div
                    className="rounded-xl border border-white/10 bg-white/5 px-5 py-4"
                    data-testid="seo-chart-clicks"
                  >
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Tendance clics</p>
                    <svg width="220" height="60" className="mt-4" data-testid="seo-chart-clicks-svg">
                      <polyline
                        fill="none"
                        stroke="#f59e0b"
                        strokeWidth="2"
                        points={buildSparkline(sparklineData.clicks)}
                      />
                    </svg>
                  </div>
                  <div
                    className="rounded-xl border border-white/10 bg-white/5 px-5 py-4"
                    data-testid="seo-chart-impressions"
                  >
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Tendance impressions</p>
                    <svg width="220" height="60" className="mt-4" data-testid="seo-chart-impressions-svg">
                      <polyline
                        fill="none"
                        stroke="#38bdf8"
                        strokeWidth="2"
                        points={buildSparkline(sparklineData.impressions)}
                      />
                    </svg>
                  </div>
                </div>

                {renderTable(data.pages.slice(0, 10), [
                  { key: "page", label: "Page" },
                  { key: "clicks", label: "Clics" },
                  { key: "impressions", label: "Impressions" },
                  {
                    key: "ctr",
                    label: "CTR",
                    format: (value) => `${Math.round((value || 0) * 100)}%`,
                  },
                  {
                    key: "position",
                    label: "Position",
                    format: (value) => Number(value || 0).toFixed(1),
                  },
                ])}
              </div>
            )}

            {activeTab === "opportunities" &&
              renderTable(data.opportunities.slice(0, 12), [
                { key: "query", label: "Mot-clé" },
                { key: "impressions", label: "Impressions" },
                {
                  key: "ctr",
                  label: "CTR",
                  format: (value) => `${Math.round((value || 0) * 100)}%`,
                },
                {
                  key: "position",
                  label: "Position",
                  format: (value) => Number(value || 0).toFixed(1),
                },
              ])}

            {activeTab === "issues" &&
              renderTable(data.issues.slice(0, 12), [
                { key: "page", label: "Page" },
                { key: "impressions", label: "Impressions" },
                {
                  key: "position",
                  label: "Position",
                  format: (value) => Number(value || 0).toFixed(1),
                },
              ])}

            {activeTab === "content" &&
              renderTable(data.pages.slice(0, 12), [
                { key: "page", label: "Page" },
                { key: "clicks", label: "Clics" },
                { key: "impressions", label: "Impressions" },
              ])}

            {activeTab === "keywords" &&
              renderTable(data.queries.slice(0, 12), [
                { key: "query", label: "Mot-clé" },
                { key: "clicks", label: "Clics" },
                { key: "impressions", label: "Impressions" },
                {
                  key: "ctr",
                  label: "CTR",
                  format: (value) => `${Math.round((value || 0) * 100)}%`,
                },
              ])}

            {activeTab === "generate" && (
              <div className="space-y-6" data-testid="seo-generate">
                <div className="rounded-xl border border-white/10 bg-white/5 p-6">
                  <p className="text-sm font-semibold text-slate-100 mb-4" data-testid="seo-generate-title">
                    Générer une page HTML
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Langue</label>
                      <Select
                        value={generateForm.language}
                        onValueChange={(value) => handleGenerateChange("language", value)}
                      >
                        <SelectTrigger data-testid="seo-generate-language">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fr">FR</SelectItem>
                          <SelectItem value="en">EN</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Ville</label>
                      <Input
                        value={generateForm.city}
                        onChange={(event) => handleGenerateChange("city", event.target.value)}
                        data-testid="seo-generate-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Service</label>
                      <input
                        list="seo-service-options"
                        value={generateForm.service}
                        onChange={(event) => handleGenerateChange("service", event.target.value)}
                        className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                        data-testid="seo-generate-service"
                      />
                      <datalist id="seo-service-options">
                        {SERVICE_OPTIONS.map((option) => (
                          <option key={option} value={option} />
                        ))}
                      </datalist>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Mot-clé principal</label>
                      <Input
                        value={generateForm.primary_keyword}
                        onChange={(event) => handleGenerateChange("primary_keyword", event.target.value)}
                        data-testid="seo-generate-primary"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Mots-clés secondaires</label>
                      <Input
                        value={generateForm.secondary_keywords}
                        onChange={(event) => handleGenerateChange("secondary_keywords", event.target.value)}
                        data-testid="seo-generate-secondary"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs uppercase tracking-[0.2em] text-slate-400">Notes</label>
                      <Textarea
                        rows={3}
                        value={generateForm.notes}
                        onChange={(event) => handleGenerateChange("notes", event.target.value)}
                        data-testid="seo-generate-notes"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-4">
                    <Button
                      className="rounded-none uppercase tracking-wider font-bold bg-white text-slate-900"
                      onClick={handleGenerate}
                      disabled={generating}
                      data-testid="seo-generate-submit"
                    >
                      {generating ? "Génération..." : "Générer HTML"}
                    </Button>
                    <Button
                      variant="outline"
                      className="rounded-none uppercase tracking-wider font-bold border-white/30 text-white"
                      onClick={() => navigator.clipboard.writeText(generatedHtml)}
                      disabled={!generatedHtml}
                      data-testid="seo-generate-copy"
                    >
                      Copier
                    </Button>
                    <Button
                      variant="outline"
                      className="rounded-none uppercase tracking-wider font-bold border-white/30 text-white"
                      onClick={downloadGeneratedHtml}
                      disabled={!generatedHtml}
                      data-testid="seo-generate-download"
                    >
                      Télécharger HTML
                    </Button>
                  </div>
                  <Textarea
                    className="mt-4"
                    rows={10}
                    value={generatedHtml}
                    readOnly
                    data-testid="seo-generate-output"
                  />
                </div>
                <div className="space-y-4">
                  {data.generated.map((item, index) => (
                    <div
                      key={`${item.suggestion}-${index}`}
                      className="rounded-xl border border-white/10 bg-white/5 px-5 py-4"
                      data-testid={`seo-generated-${index}`}
                    >
                      <p className="text-sm text-slate-200">{item.suggestion}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        Impressions: {item.impressions} · CTR: {Math.round(item.ctr * 100)}%
                      </p>
                    </div>
                  ))}
                  {data.generated.length === 0 && (
                    <p className="text-sm text-slate-400" data-testid="seo-generated-empty">
                      Aucune suggestion pour le moment.
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "competitors" && (
              <div className="space-y-4" data-testid="seo-competitors">
                <div className="flex flex-wrap gap-2">
                  <Input
                    value={competitorUrl}
                    onChange={(event) => setCompetitorUrl(event.target.value)}
                    placeholder="https://example.com"
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 w-64"
                    data-testid="seo-competitor-input"
                  />
                  <Button
                    className="rounded-none uppercase tracking-wider font-bold bg-white text-slate-900"
                    onClick={addCompetitor}
                    data-testid="seo-competitor-add"
                  >
                    Ajouter
                  </Button>
                </div>
                {data.competitors.length === 0 ? (
                  <p className="text-sm text-slate-400" data-testid="seo-competitor-empty">
                    Aucun concurrent enregistré.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {data.competitors.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-5 py-3"
                        data-testid={`seo-competitor-${item.id}`}
                      >
                        <span className="text-sm text-slate-200">{item.url}</span>
                        <Button
                          variant="outline"
                          className="rounded-none uppercase tracking-wider font-bold text-xs border-white/40 text-white hover:bg-white/10"
                          onClick={() => removeCompetitor(item.id)}
                          data-testid={`seo-competitor-remove-${item.id}`}
                        >
                          Supprimer
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "indexation" && (
              <div className="space-y-4" data-testid="seo-indexation">
                <div className="rounded-xl border border-white/10 bg-white/5 px-5 py-4">
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">URLs à suivre</p>
                  <div className="mt-4 space-y-3">
                    {INDEXATION_URLS.map((item, index) => (
                      <div
                        key={item.url}
                        className="flex flex-col gap-1 rounded-lg border border-white/10 bg-white/5 px-4 py-3"
                        data-testid={`seo-indexation-item-${index}`}
                      >
                        <span className="text-xs text-slate-400">{item.url}</span>
                        <span
                          className={`text-xs font-semibold ${
                            item.status === "Indexé" ? "text-emerald-300" : "text-amber-300"
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "technical" && (
              <div className="space-y-4" data-testid="seo-technical">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-xl border border-white/10 bg-white/5 px-5 py-4">
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Propriété</p>
                    <p className="text-sm text-slate-200 mt-3">{status.property || "-"}</p>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 px-5 py-4">
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Connexion GSC</p>
                    <p className="text-sm text-slate-200 mt-3">
                      {status.connected ? "Active" : "Non connectée"}
                    </p>
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 px-5 py-4" data-testid="seo-alerts">
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Alertes SEO</p>
                  {alerts.length === 0 ? (
                    <p className="text-sm text-slate-300 mt-3" data-testid="seo-alerts-empty">
                      Aucun signal critique détecté.
                    </p>
                  ) : (
                    <div className="space-y-3 mt-3">
                      {alerts.map((alert, index) => (
                        <div
                          key={`${alert.label}-${index}`}
                          className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3"
                          data-testid={`seo-alert-${index}`}
                        >
                          <span className="text-xs uppercase tracking-[0.2em] text-secondary">
                            {alert.type === "warning" ? "Alerte" : "Info"}
                          </span>
                          <div>
                            <p className="text-sm text-slate-200 font-semibold">{alert.label}</p>
                            <p className="text-xs text-slate-400">{alert.detail}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminSeoPage;